import { gsap } from 'gsap';

// Interactieve grid-canvas: vult elementen met [data-grid]. Hover laat een
// cel oplichten en faden — content kan een willekeurige kleur uit
// `data-grid-colors` zijn, óf een willekeurige afbeelding uit
// `data-grid-images` (URL-array). Variants tunen flash/fade-timings.
//
// Optionele data-attrs:
//   data-grid-variant         "default" | "mini" | "people"
//   data-grid-background      CSS-color or "transparent" (default)
//   data-grid-size-desktop    cols ≥ 768px viewport (default 20)
//   data-grid-size-mobile     cols < 768px viewport (default 8)
//   data-grid-rows-desktop    forceer rij-aantal ≥ 768px (default: afleiden uit hoogte)
//   data-grid-rows-mobile     forceer rij-aantal < 768px
//   data-grid-border-size     borderbreedte px (default 1)
//   data-grid-border-color    rgba string (default rgba(0,0,0,0.2))
//   data-grid-colors          JSON-array van color strings
//   data-grid-images          JSON-array van image URLs (overrules colors)
//   data-grid-idle            aantal cellen dat zonder hover random in- en
//                             uitfadet (default 0 = uit). Zodra de muis de
//                             grid raakt verdwijnen ze en volgt de grid de
//                             muis; na verlaten start de idle-loop opnieuw.

// Idle-timings: rustiger dan de hover-flits.
const IDLE = { fadeIn: 0.35, hold: [ 1.1, 1.8 ], fadeOut: 0.45, restartDelay: 1.2 };

const VARIANTS = {
	default: { fadeInDuration: 0.08,  fadeOutDuration: 0.08,   fadeOutDelay: 0.5  },
	mini:    { fadeInDuration: 0.08, fadeOutDuration:0.08, fadeOutDelay: 0.25 },
	people:  { fadeInDuration: 0.12, fadeOutDuration: 0.12, fadeOutDelay: 0.6  },
};

function debounce( func, wait ) {
	let timeout;
	return function ( ...args ) {
		clearTimeout( timeout );
		timeout = setTimeout( () => func.apply( this, args ), wait );
	};
}

function parseJsonAttr( raw, fallback ) {
	if ( ! raw ) {
		return fallback;
	}
	try {
		return JSON.parse( raw );
	} catch ( _ ) {
		try {
			return JSON.parse( raw.replace( /'/g, '"' ) );
		} catch ( __ ) {
			return fallback;
		}
	}
}

function supportsTouch() {
	return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

function preloadImages( urls ) {
	return urls
		.filter( ( url ) => typeof url === 'string' && url.length > 0 )
		.map( ( url ) => {
			const img = new Image();
			img.decoding = 'async';
			img.loading = 'eager';
			img.src = url;
			return img;
		} );
}

function initGrid( el ) {
	const defaults = {
		gridBackground:  'transparent',
		gridSizeDesktop: 20,
		gridSizeMobile:  8,
		gridBorderSize:  1,
		gridBorderColor: 'rgba(0, 0, 0, 0.2)',
		gridColors:      [ '#C5D4FF', '#B7B0FF', '#FF5FCE', '#4136FF', '#FFF751', '#87FEFF', '#C4FF3F' ],
	};

	const variantKey      = el.getAttribute( 'data-grid-variant' ) || 'default';
	const variant         = VARIANTS[ variantKey ] || VARIANTS.default;
	const gridBackground  = el.getAttribute( 'data-grid-background' ) || defaults.gridBackground;
	const gridSizeDesktop = parseInt( el.getAttribute( 'data-grid-size-desktop' ), 10 ) || defaults.gridSizeDesktop;
	const gridSizeMobile  = parseInt( el.getAttribute( 'data-grid-size-mobile' ), 10 )  || defaults.gridSizeMobile;
	const gridRowsDesktop = parseInt( el.getAttribute( 'data-grid-rows-desktop' ), 10 ) || null;
	const gridRowsMobile  = parseInt( el.getAttribute( 'data-grid-rows-mobile' ), 10 )  || null;
	const gridBorderSizeAttr = el.getAttribute( 'data-grid-border-size' );
	const gridBorderSize  = gridBorderSizeAttr === null
		? defaults.gridBorderSize
		: ( parseFloat( gridBorderSizeAttr ) || 0 );
	const gridBorderColor = el.getAttribute( 'data-grid-border-color' ) || defaults.gridBorderColor;
	const gridColors      = parseJsonAttr( el.getAttribute( 'data-grid-colors' ), defaults.gridColors );
	const gridImagesRaw   = parseJsonAttr( el.getAttribute( 'data-grid-images' ), [] );
	const gridImages      = Array.isArray( gridImagesRaw ) ? preloadImages( gridImagesRaw ) : [];
	const useImages       = gridImages.length > 0;
	const idleCount       = parseInt( el.getAttribute( 'data-grid-idle' ), 10 ) || 0;

	const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	if ( gridBackground !== 'transparent' ) {
		el.style.backgroundColor = gridBackground;
	}

	const canvas = document.createElement( 'canvas' );
	el.appendChild( canvas );
	const ctx = canvas.getContext( '2d' );

	let cols;
	let rows;
	let cellW;
	let cellH;
	let blocks = [];
	let lastHoveredIndex = null;
	let rafId = null;

	function setupGrid() {
		const rect = el.getBoundingClientRect();
		const dpr  = Math.min( window.devicePixelRatio || 1, 2 );

		const isMobile = window.innerWidth < 768;
		cols = isMobile ? gridSizeMobile : gridSizeDesktop;

		const explicitRows = isMobile ? gridRowsMobile : gridRowsDesktop;
		const cssCellSize  = rect.width / cols;

		if ( explicitRows && explicitRows > 0 ) {
			rows = explicitRows;
		} else {
			rows = Math.max( 1, Math.round( rect.height / cssCellSize ) );
		}

		const cssWidth  = rect.width;
		const cssHeight = rows * cssCellSize;

		// Wrapper-hoogte autoritair vanuit JS — voorkomt dat CSS-aspect-ratio
		// en rounding cellen rechthoekig maken.
		el.style.height = cssHeight + 'px';

		canvas.width  = Math.max( 1, Math.floor( cssWidth * dpr ) );
		canvas.height = Math.max( 1, Math.floor( cssHeight * dpr ) );
		canvas.style.height = cssHeight + 'px';

		cellW = canvas.width / cols;
		cellH = canvas.height / rows;

		blocks = [];
		for ( let y = 0; y < rows; y++ ) {
			for ( let x = 0; x < cols; x++ ) {
				blocks.push( {
					x:     x * cellW,
					y:     y * cellH,
					color: 'white',
					image: null,
					alpha: 0,
					idle:  false,
				} );
			}
		}
	}

	function draw() {
		ctx.clearRect( 0, 0, canvas.width, canvas.height );
		const borderPx = gridBorderSize * ( window.devicePixelRatio || 1 );

		for ( let i = 0; i < blocks.length; i++ ) {
			const block = blocks[ i ];

			if ( block.alpha > 0 ) {
				ctx.globalAlpha = block.alpha;
				if ( block.image ) {
					// Nog ladend: cel overslaan i.p.v. wit te flitsen.
					if ( block.image.complete && block.image.naturalWidth > 0 ) {
						// Center-crop: zelfde gedrag als CSS `object-fit: cover` zodat
						// rechthoekige bronnen niet uitgerekt worden in de vierkante cel.
						const iw   = block.image.naturalWidth;
						const ih   = block.image.naturalHeight;
						const side = Math.min( iw, ih );
						const sx   = ( iw - side ) / 2;
						const sy   = ( ih - side ) / 2;
						ctx.drawImage( block.image, sx, sy, side, side, block.x, block.y, cellW, cellH );
					}
				} else {
					ctx.fillStyle = block.color;
					ctx.fillRect( block.x, block.y, cellW, cellH );
				}
				ctx.globalAlpha = 1;
			}

			if ( borderPx > 0 ) {
				ctx.strokeStyle = gridBorderColor;
				ctx.lineWidth = borderPx;
				ctx.strokeRect( block.x, block.y, cellW, cellH );
			}
		}

		rafId = requestAnimationFrame( draw );
	}

	function fadeOut( block ) {
		gsap.to( block, {
			alpha:    0,
			duration: variant.fadeOutDuration,
			delay:    variant.fadeOutDelay,
		} );
	}

	function fillBlock( block ) {
		if ( useImages ) {
			block.image = gridImages[ Math.floor( Math.random() * gridImages.length ) ];
		} else {
			block.color = gridColors[ Math.floor( Math.random() * gridColors.length ) ];
			block.image = null;
		}
	}

	// Idle-loop: zonder hover komen er een paar random cellen in en weer uit.
	let idleCall   = null;
	let isHovering = false;
	let inView     = false;

	function idleSpawn() {
		const free = blocks.filter( ( block ) => block.alpha === 0 && ! gsap.isTweening( block ) );
		if ( free.length ) {
			const block = free[ Math.floor( Math.random() * free.length ) ];
			const hold  = gsap.utils.random( IDLE.hold[ 0 ], IDLE.hold[ 1 ] );

			fillBlock( block );
			block.idle = true;
			gsap.timeline( { onComplete: () => { block.idle = false; } } )
				.to( block, { alpha: 1, duration: IDLE.fadeIn } )
				.to( block, { alpha: 0, duration: IDLE.fadeOut }, `+=${ hold }` );
		}

		// Spawn-ritme zo dat er gemiddeld ~idleCount tegelijk zichtbaar zijn.
		const life = IDLE.fadeIn + ( IDLE.hold[ 0 ] + IDLE.hold[ 1 ] ) / 2 + IDLE.fadeOut;
		idleCall = gsap.delayedCall( ( life / idleCount ) * gsap.utils.random( 0.6, 1.4 ), idleSpawn );
	}

	function startIdle( delay = 0 ) {
		stopIdle();
		if ( ! idleCount || reduceMotion || isHovering || ! inView ) {
			return;
		}
		idleCall = gsap.delayedCall( delay, idleSpawn );
	}

	function stopIdle() {
		if ( idleCall ) {
			idleCall.kill();
			idleCall = null;
		}
	}

	function clearIdle() {
		blocks.forEach( ( block ) => {
			if ( block.idle ) {
				block.idle = false;
				gsap.to( block, { alpha: 0, duration: variant.fadeOutDuration, overwrite: true } );
			}
		} );
	}

	function onPointerEnter() {
		isHovering = true;
		stopIdle();
		clearIdle();
	}

	function onPointerLeave() {
		isHovering       = false;
		lastHoveredIndex = null;
		startIdle( IDLE.restartDelay );
	}

	function onPointerMove( event ) {
		const rect   = canvas.getBoundingClientRect();
		const scaleX = canvas.width / rect.width;
		const scaleY = canvas.height / rect.height;
		const mouseX = ( event.clientX - rect.left ) * scaleX;
		const mouseY = ( event.clientY - rect.top ) * scaleY;

		const col = Math.floor( mouseX / cellW );
		const row = Math.floor( mouseY / cellH );
		const hoveredIndex = row * cols + col;

		if (
			hoveredIndex >= 0 &&
			hoveredIndex < blocks.length &&
			hoveredIndex !== lastHoveredIndex
		) {
			const block = blocks[ hoveredIndex ];

			fillBlock( block );
			block.idle = false;

			gsap.to( block, {
				alpha:     1,
				duration:  variant.fadeInDuration,
				overwrite: true,
			} );

			fadeOut( block );
			lastHoveredIndex = hoveredIndex;
		}
	}

	if ( ! supportsTouch() && ! reduceMotion ) {
		canvas.addEventListener( 'mousemove', onPointerMove );
		if ( idleCount ) {
			canvas.addEventListener( 'mouseenter', onPointerEnter );
			canvas.addEventListener( 'mouseleave', onPointerLeave );
		}
	}

	// Idle alleen draaien als de grid in beeld is.
	if ( idleCount && ! reduceMotion && 'IntersectionObserver' in window ) {
		new IntersectionObserver( ( [ entry ] ) => {
			inView = entry.isIntersecting;
			if ( inView ) {
				startIdle();
			} else {
				stopIdle();
			}
		} ).observe( el );
	}

	const onResize = debounce( () => {
		if ( rafId ) {
			cancelAnimationFrame( rafId );
		}
		setupGrid();
		draw();
	}, 200 );
	window.addEventListener( 'resize', onResize );

	setupGrid();
	draw();
}

function initGrids( root = document ) {
	root.querySelectorAll( '[data-grid]' ).forEach( ( el ) => {
		if ( el.dataset.gridInit === 'true' ) {
			return;
		}
		el.dataset.gridInit = 'true';
		initGrid( el );
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', () => initGrids() );
} else {
	initGrids();
}

export { initGrids, initGrid };
